import { useDisclosure } from '@/hooks';
import { WorkOrderItemData } from '..';
import ActionIcon from '@/components/_common/ActionIcon';
import {
  WorkOrderItemOrigin,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Divider, Modal, Popover, Spin } from 'antd';
import { ArrowRight, Repeat } from 'react-feather';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { useWorkOrderItemsSwapOperationPart } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { useCallback } from 'react';
import {
  workorderComplementColor,
  workorderComplementLabel,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import WorkorderItemsSelectModal from '@/components/_common/WorkorderItemsSelectModal';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { Group } from '@/components/_common';
import formatterPrice from '@/utils/number/formatterPrice';
import StatusTag from '@/components/_common/StatusTag';

interface Props {
  workOrderItem: WorkOrderItemData;
}

export default function WorkOrderItemChangePart({ workOrderItem }: Props) {
  const [changePartModalOpened, changePartModalAction] = useDisclosure();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isOpened = complement?.status === WorkOrderComplementStatus.Open;
  const isAuthorized =
    complement?.status === WorkOrderComplementStatus.Authorized;

  const isItemTyped = workOrderItem.origin === WorkOrderItemOrigin.Typed;

  const disabled = isAuthorized && isItemTyped ? false : !isOpened;

  const workOrderItemSwapPart = useWorkOrderItemsSwapOperationPart();

  const onConfirmUpdatePrice = (partId: number, part: StockItem) => {
    const partPrice = part.prices.find(
      (price) => price.company.id === workorder?.company.id,
    )?.price;

    if (typeof partPrice !== 'number') {
      return onSelectPart(partId, false);
    }

    Modal.confirm({
      title: 'Atualização de preço do item',
      icon: null,
      content: (
        <>
          <p>
            Deseja Atualizar o preço do item do orçamento com o preço de venda
            da peça?
          </p>
          <Group gap={24}>
            <div>
              <p style={{ margin: 0 }}>Atual</p>
              <strong>{formatterPrice(workOrderItem.unitPrice)}</strong>
            </div>
            <ArrowRight />
            <div>
              <p style={{ margin: 0 }}>Novo</p>
              <strong>{formatterPrice(partPrice || 0)}</strong>
            </div>
          </Group>
        </>
      ),
      okText: 'Sim',
      cancelText: 'Não',
      onOk: () => onSelectPart(partId, true),
      onCancel: () => onSelectPart(partId, false),
    });
  };

  const confirmOpenChange = () => {
    if (isAuthorized) {
      Modal.confirm({
        title: 'Troca de peça',
        icon: null,
        content: (
          <>
            <p>
              Após substituir a peça digitada por uma peça cadastrada de uma OS{' '}
              <StatusTag
                color={workorderComplementColor[complement.status]}
                label={workorderComplementLabel[complement.status]}
                style={{ verticalAlign: 'top' }}
              />
              , essa troca não poderá ser desfeita.
            </p>
            <p>Deseja continuar?</p>
          </>
        ),
        okText: 'Continuar',
        onOk: changePartModalAction.open,
      });
    } else {
      changePartModalAction.open();
    }
  };

  const onSelectPart = useCallback(
    async (partId: number, updatePrice: boolean) => {
      try {
        if (!complement?.id) return;

        await workOrderItemSwapPart.mutateAsync({
          complementId: complement.id,
          newPartId: partId,
          workOrderItemId: workOrderItem.id,
          updatePrice,
        });

        changePartModalAction.close();
      } catch {}
    },
    [complement],
  );

  return (
    <>
      <Divider type="vertical" />

      <Popover
        content={
          !disabled ? (
            'Troca de peça'
          ) : (
            <div style={{ maxWidth: 200 }}>
              <strong>Troca de peça</strong>
              {!isItemTyped && (
                <span style={{ display: 'block' }}>
                  Peça cadastrada somente em OS aberta
                </span>
              )}
              {isItemTyped && (
                <span style={{ display: 'block' }}>
                  Peça digitada somente em OS aberta e autorizada
                </span>
              )}
            </div>
          )
        }
      >
        <div>
          <ActionIcon
            onClick={confirmOpenChange}
            disabled={
              workOrderItem.disableChangePart ||
              workOrderItem.type !== WorkOrderItemType.Part ||
              !hasPermissions([
                Authorities.MOVIMENTATION_WORKORDER_EDIT_ITEM,
                Authorities.MOVIMENTATION_WORKORDER_OPENED_EDIT_ITEM,
              ]) ||
              workOrderItemSwapPart.isLoading ||
              disabled
            }
          >
            {workOrderItemSwapPart.isLoading && <Spin size="small" />}
            {!workOrderItemSwapPart.isLoading && <Repeat size={20} />}
          </ActionIcon>
        </div>
      </Popover>

      <WorkorderItemsSelectModal
        onClose={changePartModalAction.close}
        opened={changePartModalOpened}
        modeType="single"
        onSingleSelect={onConfirmUpdatePrice}
        loading={workOrderItemSwapPart.isLoading}
        value={[workOrderItem.itemId]}
        companyId={workorder?.company.id}
        vehicle={
          !!workorder?.vehicle
            ? { id: workorder.vehicle.id, model: workorder.vehicle.model }
            : undefined
        }
      />
    </>
  );
}
